import React, { useState, useEffect } from "react";
import { Row, Col, Spin, Form, Card, Select, InputNumber } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import MainLayout from "../../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import cnt from "../../../../services/definedConstant";
import { rightAction } from "../../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../../treasuryContractBooking.config";
import { BOM } from "../../../../services/definedMethod";

const Tip = ({ text, tip }) => (
  <span>
    {text}
    <Tooltip title={tip}>
      <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
    </Tooltip>
  </span>
);

const CreateAuctionBid = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const [lineOptions, setLineOptions] = useState([]);
  const [portfolioOptions, setPortfolioOptions] = useState([]);
  const [counterpartyOptions, setCounterpartyOptions] = useState([]);
  const [bidTypeOptions, setBidTypeOptions] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const auctionId = location.state;
  const { userData } = props;
  const { session } = userData || {};

  useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = () => {
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetAuctions}/auctions/${auctionId}/bids/template`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Failed to load template.",
        );
        setIsLoading(false);
        return;
      }

      if (Array.isArray(res?.lines)) {
        setLineOptions(
          res.lines.map((l) => ({
            label: l.securityInstrumentName
              ? `${l.securityInstrumentName} — ${l.tenorDays} days`
              : `Line ${l.auctionLineId} — ${l.tenorDays} days`,
            value: l.auctionLineId,
          })),
        );
      }

      if (Array.isArray(res?.portfolios)) {
        setPortfolioOptions(
          res.portfolios.map((p) => ({
            label: `${p.portfolioName} (${p.portfolioCode})`,
            value: p.portfolioId,
          })),
        );
      }

      if (Array.isArray(res?.counterparties)) {
        setCounterpartyOptions(
          res.counterparties.map((c) => ({
            label: c.bankName
              ? `${c.bankName}${c.institutionCode ? ` — ${c.institutionCode}` : ""}`
              : `Counterparty ${c.id}`,
            value: c.id,
          })),
        );
      }

      if (Array.isArray(res?.bidTypeOptions)) {
        setBidTypeOptions(
          res.bidTypeOptions.map((b) => ({
            label: b.value,
            value: b.id,
          })),
        );
      }

      setIsLoading(false);
    });
  };

  const onFinish = (values) => {
    const body = {
      session,
      method: "POST",
      auctionLineId: values.auctionLineId,
      portfolioId: values.portfolioId,
      counterpartyId: values.counterpartyId,
      bidType: values.bidType,
      bidAmount: values.bidAmount,
      bidRate: values.bidRate,
    };

    const url = `${apiConfig.GetAuctions}/auctions/${auctionId}/bids`;

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Failed to create auction bid.",
        );
        setIsLoading(false);
        return;
      }
      BOM.AlertMsg(cnt.SUCCESS, "Auction bid created successfully.");
      setIsLoading(false);
      navigate(-1);
    });
  };

  return (
    <MainLayout pageItems={staticConfig.newAuction}>
      <TopContent pageTitle={labelConfig.auction} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
              Create Auction Bid
            </h3>

            <Form layout="vertical" form={form} onFinish={onFinish}>
              {/* Auction Line & Portfolio */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Auction Line"
                        tip="The auction line this bid is being placed against."
                      />
                    }
                    name="auctionLineId"
                    rules={[
                      { required: true, message: "Auction line is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={lineOptions}
                      placeholder="Select Auction Line"
                      showSearch
                      optionFilterProp="label"
                      loading={isLoading}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Portfolio"
                        tip="The portfolio placing this auction bid."
                      />
                    }
                    name="portfolioId"
                    rules={[
                      { required: true, message: "Portfolio is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={portfolioOptions}
                      placeholder="Select Portfolio"
                      showSearch
                      optionFilterProp="label"
                      loading={isLoading}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Counterparty"
                        tip="The counterparty submitting this bid."
                      />
                    }
                    name="counterpartyId"
                    rules={[
                      { required: true, message: "Counterparty is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={counterpartyOptions}
                      placeholder="Select Counterparty"
                      showSearch
                      optionFilterProp="label"
                      loading={isLoading}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Bid Type"
                        tip="Whether the bid is from a Bank or a Client."
                      />
                    }
                    name="bidType"
                    rules={[
                      { required: true, message: "Bid type is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={bidTypeOptions}
                      placeholder="Select Bid Type"
                      loading={isLoading}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Bid Amount"
                        tip="The face value amount being bid at this auction."
                      />
                    }
                    name="bidAmount"
                    rules={[
                      { required: true, message: "Bid amount is required" },
                      {
                        type: "number",
                        min: 1,
                        message: "Must be greater than 0",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 500,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Bid Rate (%)"
                        tip="The yield / discount rate at which the bid is placed."
                      />
                    }
                    name="bidRate"
                    rules={[
                      { required: true, message: "Bid rate is required" },
                      {
                        type: "number",
                        min: 0,
                        message: "Must be 0 or greater",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 12.50"
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div
                style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      marginRight: 12,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => form.submit()}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #BA24D5",
                      background: "#BA24D5",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    Submit
                  </button>
                </Col>
              </Row>

              <button type="submit" hidden />
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(CreateAuctionBid);
